import React from "react";
import withRouter from 'js/withRouter';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Button, Spinner, FormSelect } from 'react-bootstrap';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import { generateImageFromPrompt, getUserPriceInfoAction } from '../helpers/generativeImageService';
import ReferenceImageView from "./ReferenceImageView";
import { Modal } from "react-bootstrap";
import MediaLibraryCreacheck from '../../components/layout/mediaLibrary/MediaLibraryCreacheck';


class EditorGenerativeImageCreation extends React.PureComponent {


  // Constructor to initialize state
  constructor(props) {
    super(props);
    this.state = {
      prompt: '',
      isGenerating: false,
      generationError: null,
      aspectRatio: '16:9',
      isUsable: true,
      referenceImages: [],
      imageGenerationPrice: null,
      userCredits: null,
      imageConfirm: false,
      generatedImageUrl: null,
      generatedImageWidth: null,
      generatedImageHeight: null,
      showMediaLibraryModal: false,
    };
    this.fileInputRef = React.createRef();
  }

  // Lifecycle method to check if the generative image service can be used based on the needed image dimensions
  componentDidMount() {
    getUserPriceInfoAction().then(info => {
      this.setState({ imageGenerationPrice: info.generation_price, userCredits: info.credits });
      console.log('Fetched image generation price:', info.generation_price);
      console.log('Fetched user credits:', info.credits);
    }).catch(error => {
      console.error('Error fetching user image generation price info:', error);
    });

    const neededWidth = this.props.neededWidth || 800;
    const neededHeight = this.props.neededHeight || 450;
    console.log('EditorGenerativeImageCreation mounted with neededWidth:', neededWidth, 'and neededHeight:', neededHeight);

    const totalPixels = neededWidth * neededHeight;
    console.log('Total pixels required for image generation:', totalPixels);

    const maxPixels = 3000 * 3000; // Example limit of 9 million pixels
    if (totalPixels > maxPixels) {
      console.warn('Requested image size exceeds the maximum allowed pixels. Disabling generative image service.');
      this.setState({ isUsable: false });
    } else {
      const aspectRatio = this.findSuitableAspectRatio(neededWidth, neededHeight);
      this.setState({ aspectRatio });
    }
  }

  // Handler function to generate an image based on the prompt and aspect ratio
  handleGenerateImage = async (prompt) => {
    try {
      this.setState({ isGenerating: true });
      const imageData = await generateImageFromPrompt(prompt, this.state.aspectRatio, this.state.referenceImages);
      const imageUrl = imageData.image_data_url;
      const width = imageData.width;
      const height = imageData.height;
      console.log('Generated image data URL:', imageUrl);
      console.log('Generated image dimensions:', width, 'x', height);

      this.setState({ generatedImageUrl: imageUrl, generatedImageWidth: width, generatedImageHeight: height, imageConfirm: true });

      // this.props.loadMediaFromDataUrl(imageUrl, width, height);
      this.setState({ isGenerating: false });
    } catch (error) {
      console.error('Error generating image:', error);
      this.setState({ isGenerating: false, generationError: 'Failed to generate image. Please try again.' });
    }
  };

  // Handler function to trigger the file input for adding reference images
  handleAddReferenceImage = () => {
    this.fileInputRef.current.click();
  };

  // Handler function to process the selected reference images, resize them if necessary, and convert them to data URLs for use in image generation
  handleFileInputChange = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const referenceImages = Array.from(files).map(file => {
        let image = new Image();
        const objectUrl = URL.createObjectURL(file);
        image.src = objectUrl;
        image.onload = () => {
          URL.revokeObjectURL(objectUrl);
          if (image.width > 1000 || image.height > 1000) {
            console.warn(`Reference image ${file.name} is too large (${image.width}x${image.height}). Consider resizing it for better performance.`);
            let scaleFactor = Math.min(1000 / image.width, 1000 / image.height);
            let canvas = document.createElement('canvas');
            canvas.width = image.width * scaleFactor;
            canvas.height = image.height * scaleFactor;
            let ctx = canvas.getContext('2d');
            ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
            const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
            console.log(`Resized reference image ${file.name} to ${canvas.width}x${canvas.height} for better performance.`);
            this.setState(prevState => ({
              referenceImages: [...prevState.referenceImages, { dataUrl: resizedDataUrl }]
            }));
          }
          else {
            let canvas = document.createElement('canvas');
            canvas.width = image.width;
            canvas.height = image.height;
            let ctx = canvas.getContext('2d');
            ctx.drawImage(image, 0, 0);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
            this.setState(prevState => ({
              referenceImages: [...prevState.referenceImages, { dataUrl: dataUrl }]
            }));
          }
        };
      });
    }
  };

  handleReferenceFromMediaLibary = (fileKey, s3Url, fileName, thumb, copyRight) => {
    const url = `${s3Url}${fileKey}`;

    let image = new Image();
    image.src = url;
    image.crossOrigin = "Anonymous"; // This is important to avoid CORS issues when loading images from S3
    image.onload = () => {
      // Get the dimensions of the loaded image
      const width = image.width;
      const height = image.height;
      console.log(`Reference image from media library ${fileName} loaded with dimensions: ${width}x${height}`);

      // Create a canvas to convert the image to a data URL
      let canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      let ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);

      this.setState(prevState => ({
        referenceImages: [...prevState.referenceImages, { dataUrl: dataUrl }],
        showMediaLibraryModal: false
      }));
    }
  };

  clearReferenceImages = () => {
    this.setState({ referenceImages: [] });
  };

  // Handler function to update the prompt state when the user types in the textarea
  handlePromptChange = (event) => {
    this.setState({ prompt: event.target.value });
  };

  // Handler function to remove a reference image from the state when the user clicks the remove button
  handleRemoveReferenceImage = (index) => {
    this.setState(prevState => {
      const updatedReferenceImages = [...prevState.referenceImages];
      updatedReferenceImages.splice(index, 1);
      return { referenceImages: updatedReferenceImages };
    });
  };

  // Helper function to determine the most suitable aspect ratio based on the needed width and height
  findSuitableAspectRatio = (width, height) => {
    const aspectRatio = width / height;
    if (aspectRatio > 1.7) {
      return '16:9';
    } else if (aspectRatio < 0.6) {
      return '9:16';
    } else if (aspectRatio > 1.3) {
      return '4:3';
    } else if (aspectRatio < 0.8) {
      return '3:4';
    } else {
      return '1:1';
    }
  };

  // Predefined aspect ratio options for the user to select from
  aspectRatios = [
    { label: '16:9', value: '16:9' },
    { label: '9:16', value: '9:16' },
    { label: '4:3', value: '4:3' },
    { label: '3:4', value: '3:4' },
    { label: '1:1', value: '1:1' },
  ];

  // Helper function to determine if the user has sufficient credits to generate an image based on the fetched price and user credits
  hasSufficientCredits = () => {
    const { userCredits, imageGenerationPrice } = this.state;
    return userCredits !== null && imageGenerationPrice !== null && userCredits >= imageGenerationPrice;
  };

  // Helper function to get the appropriate text for the generate button based on the generation state and price information
  getGenerateButtonText = () => {
    const { messages } = this.props.intl;
    const { imageGenerationPrice } = this.state;

    // First case: If an image is currently being generated, show a loading state
    if (this.state.isGenerating) {
      return messages.editor.generating_image || "Generating Image...";
    }

    // Second case: If price information is not available yet, show a loading state
    if (imageGenerationPrice === null) {
      return messages.editor.loading_price_info || "Loading price info...";
    }

    // Third case: If the user does not have sufficient credits, show an appropriate message
    if (!this.hasSufficientCredits()) {
      return messages.editor.insufficient_credits || "Insufficient Credits";
    }

    // Default case: Show the generate button text with the price information if available
    const baseText = messages.editor.generate_image || "Generate Image";
    if (imageGenerationPrice !== null) {
      return `${baseText} (${imageGenerationPrice} credits)`;
    }

    return baseText;
  };

  // Handler function to confirm the generated image and load it into the editor when the user clicks the confirm button
  confirmGeneratedImage = () => {
    const { generatedImageUrl, generatedImageWidth, generatedImageHeight } = this.state;
    this.props.loadMediaFromDataUrl(generatedImageUrl, generatedImageWidth, generatedImageHeight);
    if (this.props.handleAiGeneratedImage) {
      this.props.handleAiGeneratedImage(true); // Notify the parent component that the image was generated by AI
    }
  };

  // Handler function to reset the state for image confirmation and clear reference images when the user clicks the regenerate button
  regenerateImage = () => {
    this.clearReferenceImages();
    this.setState({
      referenceImages: [{ dataUrl: this.state.generatedImageUrl, width: this.state.generatedImageWidth, height: this.state.generatedImageHeight }],
      imageConfirm: false,
      generatedImageUrl: null,
      generatedImageWidth: null,
      generatedImageHeight: null,
    });
    console.log(`Reference Image Count after regeneration: ${this.state.referenceImages.length}`);
  };

  // Render method to display the controls for generating an image, including the prompt textarea, reference image upload, aspect ratio selection, and generate button
  renderImageGenerationControls(messages) {
    return (
      <>
        <div className="generative-image-service p-1 dflex flex-column align-items-center">
          <h5>{messages.editor.generative_image_service || "Generative Image Service"}</h5>
          <form className="w-50">
            <div style={{ marginBottom: "10px" }}>
              <textarea
                className="form-control mb-3"
                placeholder={messages.editor.generative_image_prompt_placeholder || "Enter a description for the image you want to generate..."}
                rows={4}
                value={this.state.prompt}
                onChange={this.handlePromptChange}
                disabled={this.state.isGenerating}
              />
            </div>
            <div className="d-flex flex-column align-items-center" style={{ marginBottom: "10px" }}>
              <h6>{messages.editor.reference_images || "Insert reference images to guide the image generation (optional)."}</h6>
              <div className="d-flex flex-row align-items-center justify-content-center" style={{ marginBottom: "10px" }}>
                <ButtonGroup>
                  <Button variant="secondary" size="sm" onClick={this.handleAddReferenceImage} disabled={this.state.isGenerating}>
                    {messages.editor.add_reference_image || "Add Reference Images"}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => this.setState({ showMediaLibraryModal: true })} disabled={this.state.isGenerating}>
                    {messages.editor.from_media || "From Media Library"}
                  </Button>
                </ButtonGroup>
              </div>
              <div className="w-100" style={{ marginBottom: "10px" }}>
                <ReferenceImageView
                  referenceImages={this.state.referenceImages}
                  messages={messages}
                  onRemoveReferenceImage={this.handleRemoveReferenceImage}
                  onAddReferenceImages={this.handleAddReferenceImage} />
              </div>
              <input type="file" accept="image/*" multiple ref={this.fileInputRef} style={{ display: 'none' }} onChange={this.handleFileInputChange} />
            </div>
            <div className="d-flex flex-column align-items-center" style={{ marginTop: "10px" }}>
              <h6>{messages.editor.select_aspect_ratio || "Select Aspect Ratio"}</h6>
              <FormSelect
                className="mb-3"
                value={this.state.aspectRatio || '1:1'}
                onChange={(e) => this.setState({ aspectRatio: e.target.value })}
                disabled={this.state.isGenerating}
                style={{ maxWidth: "200px", fontSize: "14px" }}
              >
                {this.aspectRatios.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </FormSelect>
            </div>
            <Button
              variant="primary"
              type="button"
              onClick={() => this.handleGenerateImage(this.state.prompt)}
              style={{ marginTop: "10px" }}
              disabled={this.state.isGenerating || !this.hasSufficientCredits()}
            >
              {this.state.isGenerating && <Spinner animation="border" role="status" style={{ width: "14px", height: "14px", marginRight: "10px" }} className="me-3" />}
              {this.getGenerateButtonText()}
            </Button>
          </form>
          {this.state.generationError && (
            <div className="text-danger mt-2">{this.state.generationError}</div>
          )}
        </div>
        <Modal dialogClassName="media-library-modal" size="lg" centered show={this.state.showMediaLibraryModal} onHide={() => this.setState({ showMediaLibraryModal: false })}>
          <Modal.Header closeButton>
            <Modal.Title>{messages.editor.media_library || "Media Library"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <MediaLibraryCreacheck
              handleMedia={this.handleReferenceFromMediaLibary}
              neededHeight={this.props.neededHeight}
              neededWidth={this.props.neededWidth}
              allowSmallerImages={true}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => this.setState({ showMediaLibraryModal: false })}>
              {messages.editor.close || "Close"}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  };

  // Render method to display the confirmation UI for the generated image, allowing the user to confirm or regenerate the image
  renderImageConfirmation(messages) {
    return (
      (this.state.generatedImageUrl) ? (
        <div className="generative-image-confirmation p-1 dflex flex-column align-items-center">
          <h5>{messages.editor.confirm_generated_image || "Confirm Generated Image"}</h5>
          <img src={this.state.generatedImageUrl} alt="Generated" style={{ maxWidth: "100%", maxHeight: "400px", marginBottom: "20px" }} />
          <div>
            <Button variant="success" onClick={this.confirmGeneratedImage}>
              {messages.editor.confirm || "Confirm"}
            </Button>
            <Button variant="secondary" onClick={this.regenerateImage} style={{ marginLeft: "10px" }}>
              {messages.editor.regenerate || "Regenerate"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="alert alert-danger" role="alert">
          {messages.editor.image_generation_failed || "Image generation failed. Please try again."}
        </div>
      )
    );
  };

  // Render method to display the UI for the generative image creation tab
  render() {
    const {
      intl: { messages },
    } = this.props;
    return (
      <div style={{ padding: "50px" }}>
        {!this.state.isUsable ? (
          <div className="alert alert-warning" role="alert">
            {messages.editor.generative_image_service_unavailable || "The generative image service is unavailable for the requested image size."}
          </div>
        ) : (
          (this.state.imageConfirm) ? this.renderImageConfirmation(messages) : this.renderImageGenerationControls(messages)
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
  };
};

export default withRouter(injectIntl(connect(mapStateToProps)(EditorGenerativeImageCreation)));